import { useCreateReviewRequestMutation } from "@/mutations/review-request.js";
import { zodResolver } from "@hookform/resolvers/zod";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveIcon from "@mui/icons-material/Remove";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { RhfSwitch, RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type ReactNode, useCallback, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";

const schema = z.object({
    introduction: z.string().trim().min(1),
    requests: z.array(
        z.object({
            fullName: z.string().trim().min(1),
            emailAddress: z.string().email(),
            successfullySubmitted: z.boolean().optional(),
        }),
    ),
    skipEmail: z.boolean().default(false),
});

export type CreateFieldValues = z.input<typeof schema>;
export type CreateTransformedValues = z.output<typeof schema>;

const Root = (): ReactNode => {
    const navigate = useNavigate();
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [open, setOpen] = useState(true);
    const createReviewRequestMutation = useCreateReviewRequestMutation();

    const form = useForm<CreateFieldValues, unknown, CreateTransformedValues>({
        resolver: zodResolver(schema),
        defaultValues: {
            introduction: [
                "Thank you for giving us the opportunity to guide you through sale of your property.",
                "We’d love to hear about your experience with our team!",
                "You’re feedback helps us improve our services and helps future clients know what to expect.",
            ].join(" "),
            requests: [
                {
                    fullName: "",
                    emailAddress: "",
                },
            ],
        },
    });

    const {
        fields: requestFields,
        append: appendRequest,
        remove: removeRequest,
    } = useFieldArray({
        control: form.control,
        name: "requests",
    });

    const handleSubmit = useCallback(
        async (values: CreateTransformedValues) => {
            let hadErrors = false;

            for (const [index, request] of values.requests.entries()) {
                if (request.successfullySubmitted) {
                    continue;
                }

                try {
                    await createReviewRequestMutation.mutateAsync({
                        ...request,
                        introduction: values.introduction,
                        skipEmail: values.skipEmail,
                    });
                    form.setValue(`requests.${index}.successfullySubmitted`, true);
                } catch {
                    form.setValue(`requests.${index}.successfullySubmitted`, false);
                    hadErrors = true;
                }
            }

            if (hadErrors) {
                enqueueSnackbar("Failed to generate some review links", { variant: "error" });
                return;
            }

            enqueueSnackbar("Review links have been generated", { variant: "success" });
            setOpen(false);
        },
        [createReviewRequestMutation, form.setValue],
    );

    return (
        <Dialog
            open={open}
            onClose={() => {
                setOpen(false);
            }}
            slotProps={{
                transition: {
                    onExited: () => {
                        void navigate({
                            to: "/manage/reviews/requests",
                        });
                    },
                },
                paper: {
                    component: "form",
                    onSubmit: form.handleSubmit(handleSubmit),
                    noValidate: true,
                },
            }}
            maxWidth="sm"
            fullWidth
            fullScreen={fullScreen}
        >
            <DialogTitle>Generate Review Links</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <RhfTextField
                        control={form.control}
                        name="introduction"
                        label="Introduction"
                        multiline
                        minRows={5}
                        required
                    />

                    <FormControlLabel
                        control={<RhfSwitch control={form.control} name="skipEmail" />}
                        label="Skip sending email (generate link only for manual distribution)"
                    />

                    {requestFields.map((field, index) => (
                        <Stack key={field.id} spacing={2} direction="row" alignItems="flex-start">
                            <RhfTextField
                                control={form.control}
                                label="Name"
                                name={`requests.${index}.fullName`}
                                sx={{ width: "50%" }}
                                disabled={
                                    form.formState.isSubmitting || field.successfullySubmitted
                                }
                                required
                            />
                            <RhfTextField
                                control={form.control}
                                label="Email Address"
                                name={`requests.${index}.emailAddress`}
                                sx={{ width: "50%" }}
                                disabled={
                                    form.formState.isSubmitting || field.successfullySubmitted
                                }
                                required
                            />
                            {field.successfullySubmitted ? (
                                <IconButton sx={{ mt: 1 }} color="success">
                                    <CheckCircleIcon color="success" />
                                </IconButton>
                            ) : (
                                <IconButton
                                    onClick={() => {
                                        removeRequest(index);
                                    }}
                                    disabled={
                                        requestFields.length < 2 || form.formState.isSubmitting
                                    }
                                    sx={{ mt: 1 }}
                                >
                                    <RemoveIcon />
                                </IconButton>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={() => {
                        appendRequest({ fullName: "", emailAddress: "" });
                    }}
                    color="inherit"
                    sx={{ mr: "auto" }}
                    disabled={form.formState.isSubmitting}
                >
                    Add reviewer
                </Button>

                <Button
                    onClick={() => {
                        setOpen(false);
                    }}
                    color="inherit"
                    disabled={form.formState.isSubmitting}
                >
                    Cancel
                </Button>
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Generate Links
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const Route = createFileRoute("/manage/reviews/requests/create")({
    component: Root,
});
